import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { getPlatformCode } from '../platform';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { SkeletonGrid } from '../components/Skeleton';
import { EmptyState } from '../components/UI';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import CoachMark from '../components/CoachMark';
import { StatusPill } from '../components/PlayerUI';
import { useGuestGate } from '../context/GuestGateContext';

interface Game {
  id: string;
  name: string;
  imageUrl: string | null;
  downloadLink: string | null;
  isHot?: boolean;
  credentialRequestsEnabled?: boolean;
  // Per-operator credential rules from the backend, so the UI can tell the
  // player what a valid game password looks like for this platform.
  credentialRules?: {
    password: { minLength: number; maxLength: number; alphanumericOnly: boolean; description: string };
  };
}

interface Credential {
  id: string;
  gameUsername: string;
  gamePasswordPlain: string;
  assignedAt: string;
  game: { name: string };
}

type GameStatus = 'ready' | 'pending' | 'rejected' | 'request';
type FilterTab = 'all' | 'favorites';

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

function shouldShowGame(game: Game) {
  return allowsCredentialRequests(game) || !!game.downloadLink;
}

export default function Games() {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { colors, isDark } = useTheme();
  const { alert } = useAlert();
  const toast = useToast();
  const { credentialUpdateCount } = useSocket();
  const { requireAuth } = useGuestGate();

  const [games, setGames] = useState<Game[]>([]);
  const [statusByGame, setStatusByGame] = useState<Record<string, GameStatus>>({});
  const [requestReasonsByGame, setRequestReasonsByGame] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('sls_favorite_games') || '[]'); } catch { return []; }
  });
  const [loading, setLoading] = useState(true);

  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [credLoading, setCredLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Self-serve automation state (create account, balance, reset, load-to-game)
  const [selfUsername, setSelfUsername] = useState('');
  const [selfPassword, setSelfPassword] = useState('');
  const [creatingSelf, setCreatingSelf] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<{ username?: string; password: string; kind: 'created' | 'reset' } | null>(null);
  const [resetOpenId, setResetOpenId] = useState<string | null>(null);
  const [resetPasswordInput, setResetPasswordInput] = useState('');
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [loadOpenId, setLoadOpenId] = useState<string | null>(null);
  const [loadAmount, setLoadAmount] = useState('');
  const [loadingCredId, setLoadingCredId] = useState<string | null>(null);

  // Wipe transient automation UI whenever the modal target changes so secrets
  // and inline forms never leak between games.
  useEffect(() => {
    setSelfUsername('');
    setSelfPassword('');
    setRevealedSecret(null);
    setResetOpenId(null);
    setResetPasswordInput('');
    setLoadOpenId(null);
    setLoadAmount('');
  }, [selectedGame]);

  const fetchGames = useCallback(async () => {
    try {
      // Guests aren't scoped server-side (no auth token), so preview the right
      // operator's catalog by passing the stored platform code as ?platformCode.
      // Authenticated players are scoped automatically by the backend.
      // Authenticated players MUST fetch through the authed `api` client so the
      // backend scopes /games to THEIR platform (from the JWT). Plain axios sends
      // no token → the backend falls back to the default platform's catalog
      // (the "only 3 games" bug). Guests have no token, so they preview a
      // specific operator via ?platformCode.
      const platformCode = !isLoggedIn ? getPlatformCode() : null;
      const gamesReq = isLoggedIn
        ? api.get<Game[]>('/games')
        : axios.get<Game[]>(platformCode
            ? `${API_BASE}/games?platformCode=${encodeURIComponent(platformCode)}`
            : `${API_BASE}/games`);
      const [gamesRes, credsRes, reqsRes] = await Promise.all([
        gamesReq,
        api.get<{ gameId: string }[]>('/games/my-credentials').catch(() => ({ data: [] } as any)),
        api.get<{ gameId: string; status: string; reason?: string | null }[]>('/games/my-credential-requests').catch(() => ({ data: [] } as any)),
      ]);
      setGames(gamesRes.data.filter(shouldShowGame));
      const status: Record<string, GameStatus> = {};
      const reasons: Record<string, string> = {};
      (credsRes.data || []).forEach((c: any) => { if (c.gameId) status[c.gameId] = 'ready'; });
      (reqsRes.data || []).forEach((r: any) => {
        if (!r.gameId || status[r.gameId] === 'ready' || status[r.gameId]) return;
        if (r.status === 'Pending') status[r.gameId] = 'pending';
        if (r.status === 'Rejected') {
          status[r.gameId] = 'rejected';
          reasons[r.gameId] = r.reason || 'No reason provided.';
        }
      });
      gamesRes.data.filter(allowsCredentialRequests).forEach(g => { if (!status[g.id]) status[g.id] = 'request'; });
      setStatusByGame(status);
      setRequestReasonsByGame(reasons);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  useEffect(() => {
    if (!showModal || !selectedGame) return;
    setCredLoading(true);
    api.get<Credential[]>(`/games/${selectedGame.id}/my-credentials`)
      .then(res => setCredentials(res.data))
      .catch(() => null)
      .finally(() => setCredLoading(false));
  }, [credentialUpdateCount, showModal, selectedGame]);

  const openCredentials = async (game: Game) => {
    setSelectedGame(game);
    setCredentials([]);
    setShowModal(true);
    setCredLoading(true);
    try {
      const res = await api.get<Credential[]>(`/games/${game.id}/my-credentials`);
      setCredentials(res.data);
    } catch {
      /* handled by empty state */
    } finally {
      setCredLoading(false);
    }
  };

  const handleDownload = (link: string) => window.open(link, '_blank', 'noopener');

  const createSelfCredential = async () => {
    if (!isLoggedIn) { requireAuth(); return; }
    if (!selectedGame) return;
    const username = selfUsername.trim();
    if (!username) {
      alert(t('games.selfCreate.errorTitle'), t('games.selfCreate.usernameRequired'));
      return;
    }
    // Game-credential passwords are validated by the operator PANEL, not us —
    // pass whatever the player types straight through and surface the panel's
    // own error if it rejects it. (Distinct from the SLS login password.)
    const password = selfPassword.trim();
    setCreatingSelf(true);
    try {
      const res = await api.post<{ username?: string; password?: string; queued?: boolean }>(
        `/games/${selectedGame.id}/self-credential`,
        password ? { username, password } : { username },
      );
      if (res.data?.queued) {
        alert(t('games.selfCreate.queuedTitle'), t('games.selfCreate.queuedBody'));
      } else {
        setRevealedSecret({ username: res.data?.username, password: res.data?.password || '', kind: 'created' });
      }
      setSelfUsername('');
      setSelfPassword('');
      fetchGames();
    } catch (err: any) {
      const status = err?.response?.status;
      const message: string = err?.response?.data?.message || '';
      if (status === 409 && message.includes('username_taken')) {
        alert(t('games.selfCreate.usernameTakenTitle'), t('games.selfCreate.usernameTakenMessage'));
      } else if (status === 409) {
        alert(t('games.selfCreate.alreadyHaveTitle'), t('games.selfCreate.alreadyHaveMessage'));
      } else if (status === 400 && message) {
        // Surface whatever the panel/backend actually said (e.g. the operator
        // panel's own password rule) rather than an SLS-invented message.
        alert(t('games.selfCreate.errorTitle'), message);
      } else {
        alert(t('games.selfCreate.errorTitle'), t('games.selfCreate.errorMessage'));
      }
    } finally {
      setCreatingSelf(false);
    }
  };


  const submitResetPassword = async (cred: Credential) => {
    if (!isLoggedIn) { requireAuth(); return; }
    // Panel-authoritative password — no SLS-side format rule.
    const password = resetPasswordInput.trim();
    setResettingId(cred.id);
    try {
      const res = await api.post<{ password?: string; queued?: boolean }>(
        `/games/my-credentials/${cred.id}/reset-password`,
        password ? { newPassword: password } : {},
      );
      setResetOpenId(null);
      setResetPasswordInput('');
      if (res.data?.queued) {
        alert(t('games.resetPassword.queuedTitle'), t('games.resetPassword.queuedBody'));
      } else {
        setRevealedSecret({ password: res.data?.password || '', kind: 'reset' });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const message: string = err?.response?.data?.message || '';
      if (status === 400 && message) alert(t('games.resetPassword.errorTitle'), message);
      else alert(t('games.resetPassword.errorTitle'), t('games.resetPassword.errorMessage'));
    } finally {
      setResettingId(null);
    }
  };

  const submitLoadToGame = async (cred: Credential) => {
    if (!isLoggedIn) { requireAuth(); return; }
    if (!selectedGame) return;
    const amount = Number(loadAmount);
    if (!amount || amount <= 0) {
      alert(t('games.load.invalidAmountTitle'), t('games.load.invalidAmountMessage'));
      return;
    }
    setLoadingCredId(cred.id);
    try {
      await api.post('/orders/topup', {
        gameId: selectedGame.id,
        amount,
        target: 'game',
        paymentSource: 'wallet',
      });
      setLoadOpenId(null);
      setLoadAmount('');
      toast.success(t('games.load.successTitle'), t('games.load.successMessage', { amount: amount.toFixed(2) }));
    } catch (err: any) {
      const message = err?.response?.data?.message || t('games.load.errorMessage');
      alert(t('games.load.errorTitle'), message);
    } finally {
      setLoadingCredId(null);
    }
  };

  const copyCredentialValue = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (navigator.vibrate) navigator.vibrate(40);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1600);
    } catch {
      toast.error(t('games.copy.failedTitle'), t('games.copy.failedMessage'));
    }
  };

  const submitCredentialRequest = async (game: Game) => {
    if (!isLoggedIn) { requireAuth(); return; }
    setSubmittingRequest(true);
    try {
      await api.post('/games/credential-requests', { gameId: game.id });
      alert(t('games.requestSent.title'), t('games.requestSent.message', { game: game.name }));
      setStatusByGame(prev => ({ ...prev, [game.id]: 'pending' }));
      setRequestReasonsByGame(prev => {
        const next = { ...prev };
        delete next[game.id];
        return next;
      });
      fetchGames();
    } catch {
      alert(t('games.requestError.title'), t('games.requestError.message'));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const showRejectedRequest = (game: Game) => {
    const reason = requestReasonsByGame[game.id] || t('games.rejected.noReason');
    alert(
      t('games.rejected.title'),
      t('games.rejected.message', { game: game.name, reason }),
      [
        { text: t('games.rejected.close'), style: 'cancel' },
        { text: t('games.rejected.requestAgain'), onPress: () => confirmCredentialRequest(game) },
      ],
    );
  };

  const confirmCredentialRequest = (game: Game) => {
    alert(
      t('games.confirmRequest.title'),
      t('games.confirmRequest.message', { game: game.name }),
      [
        { text: t('games.confirmRequest.cancel'), style: 'cancel' },
        {
          text: t('games.confirmRequest.send'),
          onPress: () => {
            setShowRequestModal(false);
            submitCredentialRequest(game);
          },
        },
      ],
    );
  };

  const visibleGames = games
    .filter(g => filter === 'all' || favorites.includes(g.id))
    .filter(g => g.name.toLowerCase().includes(search.trim().toLowerCase()));

  const showFloatingCta = false;

  const toggleFavorite = (gameId: string) => {
    setFavorites(current => {
      const next = current.includes(gameId) ? current.filter(id => id !== gameId) : [...current, gameId];
      try { localStorage.setItem('sls_favorite_games', JSON.stringify(next)); } catch { /* Storage may be unavailable. */ }
      return next;
    });
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        data-tour="games-header"
        style={{
          padding: '20px 20px 12px',
          paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.card,
          borderBottom: `1px solid ${colors.surfaceMuted}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: colors.text }}>{t('games.header')}</div>
          <Icon name="gamepad" size={20} color={colors.text} />
        </div>

        {(
          <div style={{
            display: 'flex', alignItems: 'center',
            backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
            borderRadius: 12, padding: '0 12px', marginTop: 12, minHeight: 44,
          }}>
            <Icon name="search" size={13} color={colors.textMuted} style={{ marginRight: 8 }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('games.searchPlaceholder')}
              autoCapitalize="none"
              style={{
                flex: 1, padding: '8px 0', fontSize: 14,
                color: colors.text, background: 'transparent', border: 'none', outline: 'none',
              }}
            />
            {search && (
              <button
                aria-label={t('games.clearSearch')}
                onClick={() => setSearch('')}
                style={{
                  width: 44, height: 44, marginRight: -12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                <Icon name="times" size={13} color={colors.textMuted} />
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {([
            { id: 'all', label: t('games.filters.all') },
            { id: 'favorites', label: t('games.filters.favorites') },
          ] as { id: FilterTab; label: string }[]).map(tab => {
            const active = filter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
                  backgroundColor: active ? colors.accent : colors.bg,
                  color: active ? '#000000' : colors.textSub,
                  fontSize: 14, fontWeight: 700,
                  minHeight: 44, cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 16 }}><SkeletonGrid items={6} /></div>
      ) : (
        <div style={{ padding: 16, paddingBottom: showFloatingCta ? 110 : 24 }}>
          {visibleGames.length === 0 ? (
            <EmptyState icon="gamepad" title={t('games.empty.title')} body={t('games.empty.body')} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {visibleGames.map((item, idx) => {
                const downloadOnly = !allowsCredentialRequests(item);
                const status = statusByGame[item.id] || 'request';
                const statusKind = downloadOnly ? 'ready' : status === 'ready' ? 'ready' : status === 'pending' ? 'processing' : 'not-set-up';
                const statusLabel = downloadOnly ? t('games.card.available') : status === 'ready' ? t('games.card.ready') : status === 'pending' ? t('games.card.pending') : t('games.card.notSetUp');
                const actionLabel = downloadOnly ? t('games.card.downloadAction') : status === 'ready' ? t('games.card.openAction') : status === 'pending' ? t('games.card.statusAction') : t('games.card.setupAction');
                const onClick = () => downloadOnly
                  ? handleDownload(item.downloadLink!)
                  : status === 'ready' ? openCredentials(item)
                    : status === 'pending' ? alert(t('games.pending.title'), t('games.pending.message', { game: item.name }))
                      : status === 'rejected' ? showRejectedRequest(item)
                        : openCredentials(item);
                return (
                  <button
                    key={item.id}
                    data-tour={idx === 0 ? 'game-card-first' : undefined}
                    onClick={onClick}
                    style={{
                      position: 'relative',
                      aspectRatio: 1,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      padding: 12, borderRadius: 14,
                      backgroundColor: colors.card,
                      border: `1px solid ${colors.line}`,
                      cursor: 'pointer', textAlign: 'center',
                      gap: 10,
                    }}
                  >
                    <span role="button" aria-label={favorites.includes(item.id) ? t('games.removeFavorite') : t('games.addFavorite')} onClick={event => { event.stopPropagation(); toggleFavorite(item.id); }} style={{ position: 'absolute', top: 8, right: 8, width: 48, height: 48, display: 'grid', placeItems: 'center' }}>
                      <Icon name="star" size={17} color={favorites.includes(item.id) ? colors.accent : colors.textMuted} />
                    </span>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" style={{ width: 62, height: 62, borderRadius: 14, objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: 62, height: 62, borderRadius: 14, backgroundColor: '#00D632',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="gamepad" size={22} color="#FFFFFF" />
                      </div>
                    )}
                    <div style={{
                      fontSize: 14, fontWeight: 800, color: colors.text,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {item.name}
                    </div>
                    <StatusPill status={statusKind} label={statusLabel} />
                    <span style={{ color: colors.accent, fontSize: 15, fontWeight: 600 }}>{actionLabel}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showFloatingCta && (
        <div
          style={{
            position: 'fixed', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480,
            bottom: `calc(68px + env(safe-area-inset-bottom, 0px))`,
            padding: 16, borderTop: `1px solid ${colors.cardBorder}`,
            backgroundColor: colors.bg, zIndex: 50,
          }}
        >
          <button
            onClick={() => setShowRequestModal(true)}
            style={{
              width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
              backgroundColor: colors.accent, color: '#000000',
              border: 'none', borderRadius: 999, padding: '16px 0',
              fontSize: 15, fontWeight: 800, cursor: 'pointer',
            }}
          >
            <Icon name="plus" size={13} color="#000000" />
            {t('games.requestCredentialsCta')}
          </button>
        </div>
      )}

      {/* Credentials Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.surfaceMuted, margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>{selectedGame?.name}</div>
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 2 }}>{t('games.credentialsModal.subtitle')}</div>
          </div>
          <button
            aria-label={t('games.credentialsModal.close')}
            onClick={() => setShowModal(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: -8, marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textMuted} />
          </button>
        </div>

        {credLoading ? (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, border: `4px solid ${colors.accentSoft}`,
              borderTopColor: colors.accentDark, borderRadius: '50%',
              animation: 'sls-spin 0.7s linear infinite',
            }} />
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 8 }}>{t('games.credentialsModal.loading')}</div>
          </div>
        ) : credentials.length === 0 ? (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
            <Icon name="key" size={36} color={colors.textMuted} />
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.textSub, marginTop: 6 }}>{t('games.credentialsModal.noCredentialsTitle')}</div>
            <div style={{ fontSize: 14, color: colors.textMuted, lineHeight: 1.5, padding: '0 16px' }}>
              {t('games.credentialsModal.noCredentialsBody')}
            </div>

            {revealedSecret ? (
              <SecretPanel secret={revealedSecret} onDone={() => setRevealedSecret(null)} colors={colors} isDark={isDark}
                copiedKey={copiedKey} onCopy={copyCredentialValue} t={t} />
            ) : selectedGame && allowsCredentialRequests(selectedGame) ? (
              <div style={{
                width: '100%', textAlign: 'left', marginTop: 8,
                borderRadius: 12, padding: 14,
                backgroundColor: colors.surfaceMuted,
                border: `1px solid ${colors.surfaceMuted}`,
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{t('games.selfCreate.title')}</div>
                <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 4, lineHeight: 1.5 }}>{t('games.selfCreate.subtitle')}</div>
                <input
                  value={selfUsername}
                  onChange={e => setSelfUsername(e.target.value)}
                  placeholder={t('games.selfCreate.usernamePlaceholder')}
                  autoCapitalize="none"
                  style={automationInputStyle(colors, isDark)}
                />
                <input
                  value={selfPassword}
                  onChange={e => setSelfPassword(e.target.value)}
                  placeholder={t('games.selfCreate.passwordPlaceholder')}
                  autoCapitalize="none"
                  style={{ ...automationInputStyle(colors, isDark), marginTop: 8 }}
                />
                <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6 }}>{t('games.selfCreate.passwordHint')}</div>
                <Button
                  label={creatingSelf ? t('games.selfCreate.creating') : t('games.selfCreate.submit')}
                  icon="user-plus"
                  loading={creatingSelf}
                  onClick={createSelfCredential}
                  style={{ marginTop: 12 }}
                />
                <button
                  disabled={submittingRequest}
                  onClick={() => confirmCredentialRequest(selectedGame)}
                  style={{
                    width: '100%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6,
                    background: 'transparent', color: colors.accentText,
                    border: 'none', marginTop: 10, minHeight: 40,
                    fontSize: 14, fontWeight: 700,
                    cursor: submittingRequest ? 'default' : 'pointer',
                    opacity: submittingRequest ? 0.5 : 1,
                  }}
                >
                  <Icon name="paper-plane" size={12} color={colors.accentText} />
                  {submittingRequest ? t('games.credentialsModal.sending') : t('games.requestCredentialsCta')}
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {revealedSecret && (
              <div style={{ marginBottom: 12 }}>
                <SecretPanel secret={revealedSecret} onDone={() => setRevealedSecret(null)} colors={colors} isDark={isDark}
                  copiedKey={copiedKey} onCopy={copyCredentialValue} t={t} />
              </div>
            )}
            {credentials.length > 0 && (
              <CoachMark
                storageKey="credentials-modal-tip-v1"
                title={t('games.coachMark.title')}
                body={t('games.coachMark.body')}
                placement="bottom"
                style={{ marginBottom: 12 }}
              />
            )}
            {credentials.map(cred => (
              <div
                key={cred.id}
                style={{
                  borderRadius: 12, padding: 14, marginBottom: 10,
                  backgroundColor: colors.surfaceMuted,
                  border: `1px solid ${colors.surfaceMuted}`,
                }}
              >
                <CredRow icon="user-circle" label={t('games.credentialsModal.username')} value={cred.gameUsername} colors={colors} />
                <div style={{ marginTop: 10 }}>
                  <CredRow icon="lock" label={t('games.credentialsModal.password')} value={cred.gamePasswordPlain} colors={colors} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  <CopyChip
                    label={copiedKey === `${cred.id}-username` ? t('games.credentialsModal.copied') : t('games.credentialsModal.username')}
                    copied={copiedKey === `${cred.id}-username`}
                    onClick={() => copyCredentialValue(`${cred.id}-username`, cred.gameUsername)}
                  />
                  <CopyChip
                    label={copiedKey === `${cred.id}-password` ? t('games.credentialsModal.copied') : t('games.credentialsModal.password')}
                    copied={copiedKey === `${cred.id}-password`}
                    onClick={() => copyCredentialValue(`${cred.id}-password`, cred.gamePasswordPlain)}
                  />
                  <CopyChip
                    label={copiedKey === `${cred.id}-both` ? t('games.credentialsModal.copied') : t('games.credentialsModal.both')}
                    copied={copiedKey === `${cred.id}-both`}
                    onClick={() => copyCredentialValue(`${cred.id}-both`, `Username: ${cred.gameUsername}\nPassword: ${cred.gamePasswordPlain}`)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10, marginBottom: 10 }}>
                  <Icon name="calendar-alt" size={11} color={colors.textMuted} />
                  <div style={{ fontSize: 14, color: colors.textMuted }}>
                    {t('games.credentialsModal.added', { date: new Date(cred.assignedAt).toLocaleDateString() })}
                  </div>
                </div>
                {/* Self-serve automation actions */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={() => { setLoadOpenId(loadOpenId === cred.id ? null : cred.id); setResetOpenId(null); setLoadAmount(''); }}
                    style={automationChipStyle(colors, false)}
                  >
                    <Icon name="coins" size={12} color={colors.accentText} />
                    {t('games.load.label')}
                  </button>
                  <button
                    onClick={() => { setResetOpenId(resetOpenId === cred.id ? null : cred.id); setLoadOpenId(null); setResetPasswordInput(''); }}
                    style={automationChipStyle(colors, false)}
                  >
                    <Icon name="redo" size={12} color={colors.accentText} />
                    {t('games.resetPassword.label')}
                  </button>
                </div>

                {loadOpenId === cred.id && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      value={loadAmount}
                      onChange={e => setLoadAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                      inputMode="decimal"
                      placeholder={t('games.load.amountPlaceholder')}
                      style={automationInputStyle(colors, isDark)}
                    />
                    <Button
                      label={loadingCredId === cred.id ? t('games.load.loading') : t('games.load.submit')}
                      icon="coins"
                      loading={loadingCredId === cred.id}
                      onClick={() => submitLoadToGame(cred)}
                      style={{ marginTop: 8 }}
                    />
                  </div>
                )}

                {resetOpenId === cred.id && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      value={resetPasswordInput}
                      onChange={e => setResetPasswordInput(e.target.value)}
                      autoCapitalize="none"
                      placeholder={t('games.resetPassword.passwordPlaceholder')}
                      style={automationInputStyle(colors, isDark)}
                    />
                    <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6 }}>
                      {selectedGame?.credentialRules?.password?.description ?? t('games.resetPassword.passwordHint')}
                    </div>
                    <Button
                      label={resettingId === cred.id ? t('games.resetPassword.resetting') : t('games.resetPassword.label')}
                      icon="redo"
                      loading={resettingId === cred.id}
                      onClick={() => submitResetPassword(cred)}
                      style={{ marginTop: 8 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {selectedGame?.downloadLink && (
          <Button label={t('games.credentialsModal.downloadGame')} icon="download" variant="outline" onClick={() => handleDownload(selectedGame.downloadLink!)} style={{ marginTop: 12 }} />
        )}
        <Button label={t('games.credentialsModal.close')} variant="secondary" onClick={() => setShowModal(false)} style={{ marginTop: 10 }} />
      </Modal>

      {/* Request Modal */}
      <Modal open={showRequestModal} onClose={() => setShowRequestModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.surfaceMuted, margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>{t('games.requestModal.title')}</div>
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 2 }}>{t('games.requestModal.subtitle')}</div>
          </div>
          <button
            aria-label={t('games.requestModal.close')}
            onClick={() => setShowRequestModal(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: -8, marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textMuted} />
          </button>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {games
            .filter(game => allowsCredentialRequests(game) && ['request', 'rejected'].includes(statusByGame[game.id] || 'request'))
            .map(game => (
              <button
                key={game.id}
                onClick={() => { setShowRequestModal(false); openCredentials(game); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12, marginBottom: 8, borderRadius: 12,
                  backgroundColor: colors.surfaceMuted,
                  border: `1px solid ${colors.surfaceMuted}`,
                  cursor: 'pointer', textAlign: 'left', minHeight: 64,
                }}
              >
                {game.imageUrl ? (
                  <img src={game.imageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, backgroundColor: '#00D632',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="gamepad" size={16} color="#FFFFFF" />
                  </div>
                )}
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: colors.text }}>{game.name}</div>
                <Icon name="paper-plane" size={13} color={colors.accentDark} />
              </button>
            ))}
        </div>
        <Button label={t('games.requestModal.cancel')} variant="secondary" onClick={() => setShowRequestModal(false)} style={{ marginTop: 12 }} />
      </Modal>
    </div>
  );
}

function automationInputStyle(colors: any, isDark: boolean): React.CSSProperties {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 12px', marginTop: 10,
    borderRadius: 10, fontSize: 14,
    color: colors.text, backgroundColor: colors.input,
    border: `1px solid ${colors.cardBorder}`, outline: 'none',
    fontFamily: 'inherit',
  };
}

function automationChipStyle(colors: any, disabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: colors.accentSoft, border: 'none',
    borderRadius: 8, padding: '8px 12px', minHeight: 40,
    color: colors.accentText, fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

function SecretPanel({
  secret, onDone, colors, isDark, copiedKey, onCopy, t,
}: {
  secret: { username?: string; password: string; kind: 'created' | 'reset' };
  onDone: () => void;
  colors: any; isDark: boolean;
  copiedKey: string;
  onCopy: (key: string, value: string) => void;
  t: (k: string, o?: any) => string;
}) {
  const created = secret.kind === 'created';
  return (
    <div style={{
      width: '100%', textAlign: 'left', marginTop: 8,
      borderRadius: 12, padding: 14,
      backgroundColor: colors.accentSoft,
      border: `1px solid ${colors.accentSoft}`,
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>
        {created ? t('games.selfCreate.successTitle') : t('games.resetPassword.successTitle')}
      </div>
      <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 4, lineHeight: 1.5 }}>
        {created ? t('games.selfCreate.successBody') : t('games.resetPassword.successBody')}
      </div>
      {created && secret.username && (
        <div style={{ marginTop: 10 }}>
          <CredRow icon="user-circle" label={t('games.credentialsModal.username')} value={secret.username} colors={colors} />
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <CredRow
          icon="lock"
          label={created ? t('games.credentialsModal.password') : t('games.resetPassword.newPasswordLabel')}
          value={secret.password}
          colors={colors}
        />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        {created && secret.username && (
          <CopyChip
            label={copiedKey === 'secret-username' ? t('games.credentialsModal.copied') : t('games.credentialsModal.username')}
            copied={copiedKey === 'secret-username'}
            onClick={() => onCopy('secret-username', secret.username!)}
          />
        )}
        <CopyChip
          label={copiedKey === 'secret-password' ? t('games.credentialsModal.copied') : t('games.credentialsModal.password')}
          copied={copiedKey === 'secret-password'}
          onClick={() => onCopy('secret-password', secret.password)}
        />
      </div>
      <Button
        label={created ? t('games.selfCreate.gotIt') : t('games.resetPassword.gotIt')}
        variant="outline"
        onClick={onDone}
        style={{ marginTop: 12 }}
      />
    </div>
  );
}

function CredRow({ icon, label, value, colors }: { icon: string; label: string; value: string; colors: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name={icon} size={13} color={colors.accentDark} />
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.textMuted }}>{label}</div>
      </div>
      <div style={{
        flexShrink: 1, marginLeft: 12, fontSize: 15, fontWeight: 600,
        color: colors.text, textAlign: 'right',
        wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  );
}

function CopyChip({ label, copied, onClick }: { label: string; copied: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        backgroundColor: 'var(--brand-soft)', border: 'none',
        borderRadius: 8, padding: '8px 10px', minHeight: 40,
        color: '#00D632', fontSize: 14, fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={11} color="#00D632" />
      {label}
    </button>
  );
}
